
import styled from '@emotion/styled';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import useResourceLoader from '../../util/useResourceLoader';
import Notification from '../Notification';
import Loader from '../Loader';
import { useCallback, useEffect, useState, useRef } from 'react';
import ReportsApi from '../../api/ReportsApi';
import ProgressiveBackoff from '../../util/ProgressiveBackoff';

function Dashboard({
  guestToken,
  loadingGuestToken,
  errorLoadingGuestToken,
  dashboardUuid,
  height,
  hideTitle,
  hideTab,
  hideChartControls,
  filters,
  urlParams,
}) {
  return (
    <>
      {loadingGuestToken ? (
        // Scoped to the dashboard's own box, NOT `fullscreen`. A fullscreen loader is
        // `fixed inset-0 z-50` with a blur backdrop, so while the token request was in flight —
        // and it is slow: the backend resolves the dashboard against the reports backend before
        // minting the token — it veiled the WHOLE viewport and swallowed clicks, freezing every
        // other widget on the workpage behind it. The box also reserves the dashboard's height so
        // the rest of the page does not jump when the iframe finally mounts.
        <DashboardPlaceholder height={height}>
          <Loader />
        </DashboardPlaceholder>
      ) : guestToken ? (
        <SupersetDashboard
          // The embed SDK loads /embedded/<EMBEDDED uuid>; the token response carries exactly that
          // value, already resolved by the backend. Falling back to `dashboardUuid` — which may be
          // the native uuid — asks Superset for a page that does not exist and returns a 404
          // rendered inside the frame.
          id={guestToken?.embeddedUuid}
          dashboardUuid={dashboardUuid}
          token={guestToken?.token}
          url={guestToken?.url}
          height={height}
          hideTitle={hideTitle}
          hideTab={hideTab}
          hideChartControls={hideChartControls}
          filters={filters}
          urlParams={urlParams}
        />
      ) : errorLoadingGuestToken ? (
        <Notification error={errorLoadingGuestToken} />
      ) : (
        <Notification error="Could not get dashboard from backend." />
      )}
    </>
  );
}

// Ant Design sizes `borderRadius` in unitless pixels while the theme tokens are
// authored as CSS lengths. Anything that is not a plain px/rem length is dropped
// rather than guessed at.
function cssLengthToPx(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return undefined;
  if (/rem\s*$/.test(value)) {
    const rootSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    return Math.round(n * rootSize);
  }
  if (/px\s*$/.test(value)) return Math.round(n);
  return undefined;
}

// Translates the tenant theme — the CSS custom properties UWEThemeContext paints on
// :root — into the Ant Design theme config Superset speaks. Values are read off the
// MOUNT NODE so they come back RESOLVED (`var()` chains and tenant overrides already
// applied) instead of the literal `var(--x, y)` strings the theme hooks hand to inline
// styles.
//
// `colorBgBase` / `colorTextBase` are the seeds antd derives every surface and text
// shade from, so feeding them the CARD's background/foreground — not the page canvas —
// is what makes the frame blend into the widget instead of showing Superset's stock grey.
//
// IMPORTANT — this payload REPLACES the frame's theme, it does not merge into it.
// UWEReportingBackend's `uwebackendtheming` mod already seeds the embedded bootstrap
// from the same tenant (`/template-files/style.json`), and `setThemeConfig` overwrites
// `theme_default` whole: whatever is left out here is LOST, not inherited. So every
// token that makes the frame look like the app — brand colours, font, font files,
// algorithm — has to be restated below, and has to resolve to the same values the
// backend sends, or the dashboard ends up themed differently from the page around it.
function buildSupersetThemeConfig(element) {
  if (typeof window === 'undefined' || !element) return null;

  const computed = window.getComputedStyle(element);
  const read = (...names) => {
    for (const name of names) {
      const value = (computed.getPropertyValue(name) || '').trim();
      if (value) return value;
    }
    return '';
  };

  // Dark mode is a self-contained skin that ignores the tenant theme (UWEThemeContext
  // scopes every tenant var to `:root:not(.dark)`), so the frame has to be told which of
  // the two worlds it is in.
  const isDark = document.documentElement.classList.contains('dark');
  const token = {};
  const set = (key, value) => {
    if (value) token[key] = value;
  };

  // `--color-primary` FIRST, and `--accent-color` only as the fallback. These are two
  // different roles that happen to both be brand colours: `--color-primary` is the
  // tenant's `color_primary` — the navy the app's primary buttons and the chart series
  // palette are built from — while `--accent-color` is the highlight (the floating
  // action button's red, which is usually `color_secondary`/`color_danger`).
  // Reading the accent first sent Superset the HIGHLIGHT as its primary, so the frame
  // came out branded in a colour the page only uses for accents — and, where the tenant
  // points accent and danger at the same value, `colorPrimary` and `colorError` landed
  // on one colour and "primary" and "error" became indistinguishable inside the frame.
  // This order also matches what the backend seeds the bootstrap with (`colorPrimary`
  // from `color_primary`, `colorLink` from `color_secondary`), so host and server agree.
  set('colorPrimary', read('--color-primary', '--accent-color'));
  set('colorLink', read('--color-secondary', '--accent-color', '--color-primary'));
  set('colorSuccess', read('--color-success'));
  set('colorWarning', read('--color-warning'));
  set('colorError', read('--color-danger'));
  set('colorInfo', read('--color-info'));
  // `--cf-Card-background` only exists once the tenant paints the Card explicitly in the
  // Theme Editor; otherwise the card surface is the `bg-white dark:bg-zinc-900` pair.
  const cardBg = read('--cf-Card-background') || (isDark ? '#18181b' : '#ffffff');
  set('colorBgBase', cardBg);
  // Pinned rather than left to antd's derivation: `colorBgLayout` is the dashboard grid
  // canvas and `colorBgContainer` the surface of its header bar and chart cards. Landing
  // both on the card's own colour is what removes Superset's stock grey gutter and lets
  // its header read as the card's header — the same flat surface the WorkTray's table sits on.
  set('colorBgLayout', cardBg);
  set('colorBgContainer', cardBg);
  set('colorTextBase', read('--cf-Card-textColor', '--text-primary'));
  // The dashboard header draws its own bottom rule from `colorBorder`, so this is what
  // makes that divider match the separators on our side of the frame.
  set('colorBorder', read('--cf-Card-borderColor', '--border-color'));
  set('colorSplit', read('--cf-Card-borderColor', '--border-color'));
  set('fontFamily', read('--font-sans', '--font-family'));

  // Naming the family is not enough: a cross-origin frame does not inherit the host
  // document's @font-face rules, and Superset only bundles Inter and IBM Plex Mono. So
  // a tenant on, say, Montserrat asked the frame for a family it had no file for and the
  // browser silently fell back to the default sans — the app in Montserrat, the dashboard
  // beside it in something else, which is the tell that gives the embed away.
  //
  // `fontUrls` is the frame's own hook for this: its ThemeController calls `loadFonts()`
  // on every applied theme and injects each URL as a stylesheet INSIDE the iframe.
  // The list is taken from the `<link>` UWEThemeContext already injects for the tenant's
  // `fonts.fonts-url`, so the font follows the Theme Editor rather than being restated
  // here — no URL is hardcoded, and a tenant that configures no web font sends nothing
  // (Superset then keeps its bundled Inter, which is also the app's default family).
  const fontUrls = typeof document !== 'undefined'
    ? Array.from(document.querySelectorAll('link[data-uwe-theme-font]'))
        .map(link => link.href)
        .filter(Boolean)
    : [];
  if (fontUrls.length) token.fontUrls = fontUrls;

  // Type SCALE and weights, not just the family. Superset sizes a chart's title, its axis
  // labels and every number it prints off these tokens, and the payload replaces
  // `theme_default` whole — so leaving them out did not "keep the tenant's sizes", it
  // dropped them and let the frame fall back to antd's stock 14px ramp and 600 bold. The
  // titles inside the frame then sat at a different size and weight than the headings of
  // the card around them, which reads as a foreign widget even once the colours match.
  //
  // Antd wants unitless px, and the tenant authors these as CSS lengths (`1rem`), so each
  // one goes through `cssLengthToPx`; a token the tenant leaves unset is simply not sent,
  // and antd derives it as before.
  const setPx = (key, value) => {
    const px = cssLengthToPx(value);
    if (px !== undefined) token[key] = px;
  };
  setPx('fontSizeXS', read('--text-xs'));
  setPx('fontSizeSM', read('--text-sm'));
  setPx('fontSize', read('--text-base', '--font-size'));
  setPx('fontSizeLG', read('--text-lg'));
  setPx('fontSizeXL', read('--text-xl'));
  setPx('fontSizeXXL', read('--text-2xl'));

  // Weights are plain numbers, not lengths, so they cannot go through `cssLengthToPx` —
  // it deliberately rejects anything without a px/rem unit and would drop all of them.
  const setWeight = (key, value) => {
    const n = parseFloat(value);
    if (Number.isFinite(n)) token[key] = n;
  };
  setWeight('fontWeightNormal', read('--font-weight-normal'));
  setWeight('fontWeightLight', read('--font-weight'));
  // What antd bolds headings and emphasis with — the one that decides how a chart title
  // reads against the card header next to it.
  setWeight('fontWeightStrong', read('--font-weight-medium', '--font-weight-bold'));
  setWeight('fontWeightBold', read('--font-weight-bold'));

  const borderRadius = cssLengthToPx(read('--radius-lg'));
  if (borderRadius !== undefined) token.borderRadius = borderRadius;

  // Superset resolves `algorithm` through its `algorithmMap`, which stock Superset keys
  // with 'default' | 'dark' | 'compact'. UWEReportingBackend's frontend patch registers a
  // fourth, 'uwe' (`registerUWEThemingAlgorithm`), and its backend already asks for it in
  // the bootstrap it seeds — it derives the neutral ramp from the tenant's own background
  // and text seeds instead of antd's greys, which is what keeps the frame's surfaces in
  // the same family as the card around it. Naming 'default' here quietly demoted the
  // frame to antd's stock ramp on every embed.
  //
  // Safe against a Superset without the plugin: the lookup is `map[name] ? map[name] :
  // defaultAlgorithm`, so an unknown name degrades to exactly the previous behaviour.
  // Dark stays on 'dark' — 'uwe' builds on antd's DEFAULT (light) algorithm.
  //
  // Only `theme_default` is sent. Leaving `theme_dark` out nulls the frame's dark theme,
  // which makes its ThemeController fall back to the default mode — so it always renders
  // the theme we just handed it instead of following a stored preference of its own. The
  // dark case is covered by re-sending a dark-seeded `theme_default`.
  return { theme_default: { token, algorithm: isDark ? 'dark' : 'uwe' } };
}

export function SupersetDashboard({ id, dashboardUuid, token, url, height, hideTitle, hideTab, hideChartControls, filters, urlParams }) {
  const ref = useRef();
  const [dashboard, setDashboard] = useState();
  const [innerHeight, setInnerHeight] = useState(height);
  const fitToHeight = !height;
  const refCallback = useCallback(
    async refElement => {
      if (!refElement) return;
      ref.current = refElement;

      // Use Idenfier (id) if available (from backend resolution), otherwise fallback to UUID
      const dashboardId = id || dashboardUuid;

      const dashboard = await embedDashboard({
        id: dashboardId,
        supersetDomain: url,
        mountPoint: refElement,
        fetchGuestToken: () => token,
        dashboardUiConfig: {
          // dashboard UI config: hideTitle, hideTab, hideChartControls, filters.visible, filters.expanded (optional), urlParams (optional)
          hideTitle,
          hideTab,
          hideChartControls,
          filters,
          urlParams: urlParams,
        },
        iframeSandboxExtras: ['allow-top-navigation', 'allow-popups-to-escape-sandbox'],
        referrerPolicy: 'origin',
      });

      setDashboard(dashboard);
    },
    [id, dashboardUuid, token, hideTitle, hideTab, hideChartControls, filters, urlParams]
  );

  // Superset 6 exposes a switchboard method for host-driven theming, surfaced by the
  // embed SDK as `setThemeConfig`. The MessagePort queues messages until the guest calls
  // start(), so sending as soon as the embed resolves is safe.
  useEffect(() => {
    if (!dashboard || typeof dashboard.setThemeConfig !== 'function') return undefined;

    // Remembers what was last sent, so the observers below can re-send on a real theme
    // change and stay quiet otherwise — Vite alone rewrites <head> constantly in dev.
    let lastSent = null;

    const applyTheme = () => {
      const themeConfig = buildSupersetThemeConfig(ref.current);
      if (!themeConfig) return;
      const signature = JSON.stringify(themeConfig);
      if (signature === lastSent) return;
      lastSent = signature;
      try {
        dashboard.setThemeConfig(themeConfig);
      } catch (e) {
        // Best-effort by design: a Superset that does not implement the method simply
        // keeps its own theme. Styling must never break a working embed.
        console.warn('[SupersetDashboard] could not apply the UWE theme to the frame', e);
      }
    };

    applyTheme();

    // `.dark` on <html> is the only signal the light/dark toggle emits, so watching that
    // class is what "the app changed appearance" looks like from in here.
    const observer = new MutationObserver(applyTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    // The tenant's web-font <link> is appended by UWEThemeContext in an effect of its
    // own, so it can land AFTER this dashboard has already sent its theme — and it is
    // replaced outright whenever the Theme Editor changes the font. Either way the frame
    // would keep a `fontUrls` that no longer matches the page. Watching <head> covers
    // both; `applyTheme` de-dupes, so the noisy dev-server churn sends nothing.
    const headObserver = new MutationObserver(applyTheme);
    headObserver.observe(document.head, { childList: true });

    return () => {
      observer.disconnect();
      headObserver.disconnect();
    };
  }, [dashboard]);

  useEffect(() => {
    if (fitToHeight && dashboard && ref.current) {
      const backoffTimeout = new ProgressiveBackoff({ initialDelay: 500, maxDelay: 5000, factor: 2 });

      async function checkHeight() {
        const { height } = await dashboard.getScrollSize();
        const iframe = ref.current.querySelector('iframe');

        if (iframe && (!innerHeight || innerHeight < height)) {
          setInnerHeight(height);
        }

        backoffTimeout.wait().then(checkHeight);
      }

      checkHeight();

      return () => {
        backoffTimeout.reset();
      };
    }
  }, [dashboard, fitToHeight, ref, innerHeight]);

  // return <div>Dashboard</div>
  return (
    <DashboardContainer ref={refCallback} height={innerHeight}>
      Dashboard
    </DashboardContainer>
  );
}

const DashboardPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: ${({ height }) => (height ? (typeof height === 'number' ? `${height}px` : height) : '300px')};
`;

/**
 * What the frame is given to render into BEFORE it has been measured.
 *
 * It used to be `100%`. The container is auto-height, so `100%` resolves against a parent with no
 * height of its own and the frame collapses to nothing — and a collapsed frame is one Superset lays
 * the dashboard out inside, at a viewport a few pixels tall. `getScrollSize()` then reports THAT as
 * the content height, so the first measurement CONFIRMS the collapse instead of correcting it, and
 * because the measurement only ever grows the embed stays at its minimum for good. A real starting
 * height breaks the loop: the dashboard lays out at a sane size, and the measurement that follows
 * reports the height it actually needs.
 */
const EMBED_START_HEIGHT = 600;

const DashboardContainer = styled.div`
  iframe {
    width: 100%;
    height: ${({ height }) =>
      height ? (typeof height === 'number' ? `${height}px` : height) : `${EMBED_START_HEIGHT}px`};
    border: 0;
  }
`;

export default Dashboard;
